import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { IRefreshTokenResponse, IUserLogin } from 'src/@types/User'
import { BASE_URL } from 'src/constants/enviroments'

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

export const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

export const createSession = async (username: string, senha: string): Promise<IUserLogin> => {
  try {
    const response = await API.post('/login', { username, senha })
    return response.data
  } catch (error) {
    throw error
  }
}

export const refreshTokenRequest = async (refreshToken: string): Promise<IRefreshTokenResponse> => {
  try {
    const response = await API.post('/refreshToken', { refreshToken })
    return response.data
  } catch (error) {
    throw error
  }
}

/*Farm created together of user on register*/
export const registerFarm = ({ descricao, ...payload }: any) => {
  return API.put('/entity/Fazenda', { descricao, proprietario: `${payload.nome} ${payload.sobrenome}` }).catch(() => {
    toast.error('Ocorreu um erro ao criar a Fazenda, tente novamente.')
  })
}

export const registerUser = (idFazenda: any, payload) => {
  return API.put(`$/entity/Usuario/username'}/${payload.username}`, { idFazenda, ...payload }).catch(() => {
    toast.error('Ocorreu um erro ao cadastrar o usuário, tente novamente.')
  })
}

API.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    if (config.skipAuth) {
      return config
    }

    if (
      config.url &&
      !config.url.includes('/login') &&
      !config.url.includes('/refreshToken') &&
      !config.url.includes('/secure')
    ) {
      const baseURL = API.defaults.baseURL || ''
      const url = new URL(config.url, baseURL)

      if (!url.pathname.startsWith('/secure')) {
        const pathParts = url.pathname.split('/').filter(Boolean)
        pathParts.splice(0, 0, 'secure')

        url.pathname = `/${pathParts.join('/')}`
        config.url = url.toString()
      }
    }

    if (config.method?.toLowerCase() === 'get' && config.url?.includes('/secure')) {
      let username = localStorage.getItem('user')
      if (username) {
        try {
          username = JSON.parse(username)
          config.params = {
            ...config.params,
            username
          }
        } catch (error) {
          console.error('Erro ao parsear dados do usuário:', error)
        }
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor de resposta para renovar token automaticamente
API.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (originalRequest.skipAuth) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/refreshToken')) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return API(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem('refreshToken')

      if (!refreshToken) {
        isRefreshing = false
        processQueue(error, null)

        localStorage.clear()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        const response = await refreshTokenRequest(refreshToken)
        const { authenticationResult } = response
        const { accessToken: newToken, refreshToken: newRefreshToken, expiresIn } = authenticationResult

        // Atualizar tokens no localStorage
        localStorage.setItem('token', newToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        const expiresToken = Date.now() + parseInt(expiresIn) * 1000
        localStorage.setItem('expiresIn', expiresToken.toString())

        // Atualizar header de autorização
        API.defaults.headers['Authorization'] = `Bearer ${newToken}`

        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`

        processQueue(null, newToken)
        isRefreshing = false

        return API(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false

        // Limpar localStorage e redirecionar para login
        localStorage.clear()
        toast.info('Sessão expirada. Faça login novamente.')
        window.location.href = '/login'

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)
